/**
 * Product-image resolution helpers shared between PLP tiles and any other surface that needs
 * to mirror the PDP main image (so a tile and the PDP it links to render the *same* hero asset).
 *
 * ## Source priority
 *
 *   0. `c_pimlyProductImage` (KL-973 / Master Catalogue EXT CR D7) — Pimly PLP tile asset(s).
 *      String or string[] from SCAPI; resolved to proxy URLs in SFRA hooks. External `http(s)://`
 *      URLs are video only when YouTube/Vimeo or a direct video file — otherwise raster image.
 *
 *   1. `c_processedImageGroups` (King Living PIM-aware preprocessed groups).
 *      Each group carries an `imageSource: 'pim' | 'cylindo'` field. Within the master `large`
 *      groups (the ones with no `variationAttributes` constraint) we deliberately prefer the
 *      one tagged `pim` — the photographic asset curated by the merch team. The Cylindo group
 *      contains static 3D-viewer frame snapshots; useful as a fallback when no PIM image is
 *      configured, but visually different from the studio photography on PDP.
 *
 *   2. Standard SCAPI `product.imageGroups`. Used when `c_processedImageGroups` is missing
 *      (older / non-King-Living catalog entries) or malformed JSON.
 *
 * ## Why this matters
 *
 * Without this priority, the SCAPI productSearch hit's `imageGroups` may surface a Cylindo URL
 * (because the catalog asset rule is keyed off the Cylindo product code) while PDP would render
 * the PIM photograph. The mismatch is jarring: a customer clicks a Cylindo render on PLP and
 * lands on a styled studio photo on PDP — same product, two visual identities. Both resolution
 * paths are now the same primitive.
 *
 * ## Alignment with PDP
 *
 * The PDP main image (`components/image-gallery/index.jsx`) reads from `c_processedImageGroups`
 * and uses `imageSource` to branch between PIM photos, PIM video, and the Cylindo 3D viewer.
 * PLP can't render the 3D viewer in a tile, so when there's no PIM master we fall through to
 * static images (Cylindo frame snapshots, then legacy SCAPI `imageGroups`). Tile slides may be
 * images or embeddable video (YouTube/Vimeo); layout follows slide count in `ProductTileImageGallery`.
 */
import {parseProcessedImageGroups} from '@amblique/king-living/app/utils/cylindo-params';
import {ensureArray} from '@amblique/king-living/app/utils/utils';
import {isEmbeddableVideoUrl} from '@amblique/king-living/app/utils/video-utils';

const LARGE = 'large';

/**
 * @param {object|null|undefined} media
 * @returns {boolean}
 */
export const isTileVideoMedia = (media) => (
  media?.mediaType === 'video' && Boolean(media?.videoUrl)
);

/**
 * @param {string} url
 * @returns {object}
 */
const mapPimlyUrlToTileMedia = (url) => {
  if (isEmbeddableVideoUrl(url)) {
    return {mediaType: 'video', videoUrl: url, alt: '', title: ''};
  }

  return {disBaseLink: url, link: url};
};

/**
 * @param {object|null|undefined} productHit - productSearch hit root or representedProduct.
 * @returns {Array<object>|null}
 */
const pickPimlyTileImages = (productHit) => {
  const raw = productHit?.c_pimlyProductImage;

  if (!raw) {
    return null;
  }

  const urls =
    typeof raw === 'string' ?
      (raw ? [raw] : []) :
      ensureArray(raw).filter(Boolean);

  if (!urls.length) {
    return null;
  }

  return urls.map(mapPimlyUrlToTileMedia);
};

/**
 * SFRA tags any `http(s)://` pimImages entry as `mediaType: 'video'`. On the tile path only
 * treat YouTube/Vimeo (or direct video files) as video — external image URLs stay raster.
 *
 * @param {object|null|undefined} media
 * @returns {object|null}
 */
const normalizeProcessedTileMedia = (media) => {
  if (!media || typeof media !== 'object') {
    return null;
  }

  if (isTileVideoMedia(media)) {
    if (isEmbeddableVideoUrl(media.videoUrl)) {
      return media;
    }

    const url = media.videoUrl;

    return {
      alt: media.alt ?? '',
      disBaseLink: url,
      link: url,
      title: media.title ?? '',
    };
  }

  if (media.disBaseLink || media.link) {
    return media;
  }

  return null;
};

/**
 * @param {object|null|undefined} shape - One product-shaped object (search hit root or nested).
 * @returns {object|null}
 */
const pickMasterLargeFromProductShape = (shape) => {
  if (!shape || typeof shape !== 'object') {
    return null;
  }

  // 1. King Living preprocessed groups (PIM-first).
  const processedGroups = parseProcessedImageGroups(shape);

  if (processedGroups.length > 0) {
    const masterLarge = processedGroups.filter(
      (g) => g?.viewType === LARGE && !g.variationAttributes
    );

    const pim = masterLarge.find(
      (g) => g.imageSource === 'pim' && g.images?.length > 0
    );

    if (pim) return pim;

    // No PIM master configured — accept any master large group with images (typically the
    // Cylindo frames). Worse than a real photograph, but better than a broken tile.
    const anyMaster = masterLarge.find((g) => g.images?.length > 0);

    if (anyMaster) return anyMaster;
  }

  // 2. Legacy SCAPI `imageGroups`. Master first; otherwise any large group with images.
  if (Array.isArray(shape.imageGroups)) {
    const master = shape.imageGroups.find(
      (g) => g?.viewType === LARGE && !g.variationAttributes && g.images?.length > 0
    );

    if (master) return master;

    const any = shape.imageGroups.find(
      (g) => g?.viewType === LARGE && g.images?.length > 0
    );

    if (any) return any;
  }

  return null;
};

/**
 * Resolve a product's hero image *group* — `viewType: 'large'`, master (no `variationAttributes`).
 *
 * Returns the group rather than the image array so callers can read `imageSource` / `variationAttributes`
 * for telemetry or further branching. For a render-ready image array, prefer `getMasterTileImages`.
 *
 * `productSearch` hits often nest `c_processedImageGroups` / `imageGroups` on `representedProduct`
 * only; `getProduct` / product-by-id responses usually carry them on the root. We try both so
 * header typeahead and PLP stay consistent with Search Idle (which uses full product payloads).
 *
 * @param {object|null|undefined} product - SCAPI productSearch hit or full product.
 * @returns {object|null}                   - matched group, or `null` if no large group is found.
 */
export const resolveMasterLargeImageGroup = (product) => {
  if (!product || typeof product !== 'object') {
    return null;
  }

  return (
    pickMasterLargeFromProductShape(product) ||
    pickMasterLargeFromProductShape(product.representedProduct)
  );
};

/**
 * Render-ready master tile slides: one entry per configured asset (image or video).
 *
 * @param {object|null|undefined} product
 * @returns {Array<object>}
 */
export const getMasterTileImages = (product) => {
  if (!product || typeof product !== 'object') {
    return [];
  }

  const pimlyImages =
    pickPimlyTileImages(product) ||
    pickPimlyTileImages(product.representedProduct);

  if (pimlyImages?.length) {
    return pimlyImages;
  }

  const images = resolveMasterLargeImageGroup(product)?.images ?? [];

  return images.map(normalizeProcessedTileMedia).filter(Boolean);
};
